import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Layers, BarChart3 } from 'lucide-react';
import { api, type EligibleGeoclient, type ModelSummary, type PortfolioReport } from '../lib/api';
import { fmtMoney } from '../lib/format';

export default function HomePage() {
  const [groups, setGroups] = useState<EligibleGeoclient[]>([]);
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [report, setReport] = useState<PortfolioReport | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.listGeoclients(), api.listModelSummaries(), api.portfolio().catch(() => null)])
      .then(([g, m, r]) => {
        setGroups(g);
        setModels(m);
        setReport(r);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="px-10 py-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-slate-900">Resource Calculator</h1>
        <p className="text-slate-500 mt-1.5 max-w-2xl">
          Right-size client teams across disciplines, levels, and offshore. Roll it all up across the
          portfolio.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <KpiTile
          label="Geoclients"
          value={loading ? '—' : String(groups.length)}
          to="/geoclients"
          icon={Building2}
          accent="purple"
        />
        <KpiTile
          label="Models"
          value={loading ? '—' : String(models.length)}
          to="/models"
          icon={Layers}
          accent="blue"
        />
        <KpiTile
          label={`Y3 portfolio opportunity (${report?.currency || 'USD'})`}
          value={
            report ? fmtMoney(report.totals.y3Dollar, report.currency || 'USD') : '—'
          }
          to="/reporting"
          icon={BarChart3}
          accent="purple"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <section className="card">
          <div className="card-header">
            <h2 className="text-base font-semibold">Geoclients</h2>
            <Link to="/geoclients" className="btn-ghost text-xs">
              Open
            </Link>
          </div>
          <div className="card-body">
            {loading ? (
              <Skeleton rows={4} />
            ) : groups.length === 0 ? (
              <EmptyState
                title="No geoclients yet"
                description="No geoclients are assigned to your account yet."
                cta={{ to: '/geoclients', label: 'Open geoclients' }}
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {groups.slice(0, 6).map((g) => (
                  <li key={g.id}>
                    <Link
                      to={`/clients/${g.clientId}?geoclientId=${encodeURIComponent(g.id)}`}
                      className="flex items-center justify-between py-3 hover:bg-slate-50 px-2 -mx-2 rounded-lg group"
                    >
                      <div>
                        <div className="font-medium text-slate-900">{g.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {g.marketName}
                          {' · '}
                          <span className="text-slate-700">{g.clientName}</span>
                        </div>
                      </div>
                      <ArrowRight
                        size={16}
                        className="text-slate-300 group-hover:text-brand-purple-500 transition-colors"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card-header">
            <h2 className="text-base font-semibold">Models</h2>
            <Link to="/models" className="btn-ghost text-xs">
              Manage
            </Link>
          </div>
          <div className="card-body">
            {loading ? (
              <Skeleton rows={4} />
            ) : models.length === 0 ? (
              <EmptyState
                title="No models yet"
                description="Create or import a model to define disciplines, levels, and coefficients."
                cta={{ to: '/models', label: 'Open model library' }}
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {models.slice(0, 6).map((m) => (
                  <li key={m.id}>
                    <Link
                      to={`/models/${m.id}`}
                      className="flex items-center justify-between py-3 hover:bg-slate-50 px-2 -mx-2 rounded-lg group"
                    >
                      <div>
                        <div className="font-medium text-slate-900">{m.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5 font-mono">{m.id}</div>
                      </div>
                      <ArrowRight
                        size={16}
                        className="text-slate-300 group-hover:text-brand-purple-500 transition-colors"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function KpiTile({
  label,
  value,
  to,
  icon: Icon,
  accent,
}: {
  label: string;
  value: string;
  to: string;
  icon: typeof Building2;
  accent: 'purple' | 'blue';
}) {
  return (
    <Link
      to={to}
      className="card px-5 py-4 hover:shadow-md transition-shadow group flex items-start gap-4"
    >
      <div
        className={
          accent === 'purple'
            ? 'w-10 h-10 rounded-lg bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center'
            : 'w-10 h-10 rounded-lg bg-brand-blue-50 text-brand-blue-700 flex items-center justify-center'
        }
      >
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-2xl font-semibold text-slate-900 mt-0.5 truncate">{value}</div>
      </div>
    </Link>
  );
}

function EmptyState({
  title,
  description,
  cta,
}: {
  title: string;
  description: string;
  cta: { to: string; label: string };
}) {
  return (
    <div className="py-6 text-center">
      <div className="text-sm font-medium text-slate-700">{title}</div>
      <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">{description}</p>
      <Link to={cta.to} className="btn-ghost text-xs mt-3 inline-flex">
        {cta.label}
      </Link>
    </div>
  );
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-10 rounded-lg bg-slate-100 animate-pulse" />
      ))}
    </div>
  );
}
